import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, AccountBalance } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { fetchProfile } from "store/features/profile";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { addWithdraw as addWithdrawApi, getCurrencies } from "apis";
import CurrencyAmountInput, { Currency } from "./CurrencyAmountInput";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormFields {
  amount: number;
  currency: string;
  accountNumber: string;
  accountName: string;
  routingNumber: string;
  comment: string;
}

const AddWithdrawDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      currency: "",
      amount: "" as any,
    },
  });
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const currencyValue = watch("currency");
  const amountValue = watch("amount");

  useEffect(() => {
    register("currency", { required: true });
    register("amount", { required: true });
  }, [register]);

  useEffect(() => {
    getCurrencies().then((data: Currency[]) => {
      setCurrencies(data);
      // Default to the first currency once the list arrives. After the
      // 05-03 cleanup the DB only has DZD + USD, so this picks DZD.
      if (data.length > 0 && !currencyValue) {
        setValue("currency", data[0].code, { shouldValidate: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCurrencyChange = (e: any) =>
    setValue("currency", e.target.value, { shouldValidate: true });
  const onAmountChange = (e: any) =>
    setValue("amount", e.target.value, { shouldValidate: true });

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    setSubmitting(true);
    addWithdrawApi({ ...data, currency: currencyValue })
      .then(({ data }) => {
        dispatch(fetchProfile(data));
        enqueueSnackbar(t("withdraw.added"), { variant: "success" });
        onClose();
      })
      .catch((err) => {
        if (err?.response?.status === 400) {
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AccountBalance />
          <Typography variant="h6" fontWeight={700}>
            {t("withdraw.add")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={2}>
            <CurrencyAmountInput
              currencies={currencies}
              currencyValue={currencyValue}
              amountValue={amountValue}
              onCurrencyChange={onCurrencyChange}
              onAmountChange={onAmountChange}
              currencyError={Boolean(errors.currency)}
              amountError={Boolean(errors.amount)}
            />
            <Grid item xs={12}>
              <TextField
                label={t("labels.account_name")}
                {...register("accountName", { required: true })}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("labels.routing_number")}
                {...register("routingNumber", { required: true })}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("labels.account_number")}
                {...register("accountNumber", { required: true })}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("comment")}
                {...register("comment")}
                fullWidth
                multiline
                rows={3}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.add")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default AddWithdrawDialog;
